<template>
	<div class="token-batches-table">
		<ApiTable
			:list-state="listState"
			:max-height="360">
			<Column header="Amount">
				<template #body="{ data }">
					<div class="token-batches-table__amount">
						<span class="token-batches-table__amount-strong"
							>{{ data.tokens_remaining }}/{{ data.tokens_purchased }}</span
						>
						<router-link
							class="token-batches-table__amount-id"
							:to="{
								name: props.routeName,
								params: props.routeParams,
								query: { tab: 'usage', s: data.uuid },
							}"
							>{{ data.uuid }}</router-link
						>
					</div>
				</template>
			</Column>
			<Column
				header="Purchase"
				style="width: 220px">
				<template #body="{ data }">
					<div class="token-batches-table__amount">
						<span class="token-batches-table__purchase">{{ formatHuman(data.purchased_at) }}</span>
						<router-link
							class="token-batches-table__amount-id"
							:to="{
								name: props.routeName,
								params: props.routeParams,
								query: { tab: 'purchases', s: data.purchase_id },
							}"
							>{{ data.purchase_id }}</router-link
						>
					</div>
				</template>
			</Column>
			<Column
				header="Expiry Date"
				style="width: 220px">
				<template #body="{ data }">
					{{ formatHuman(data.expiry_date) }}
				</template>
			</Column>
		</ApiTable>
	</div>
</template>

<script setup lang="ts">
import useApiTable from '@/components/Table/useApiTable'
import { useTokenBatchListState } from '@/models/TokenBatch/States'
import { watch } from 'vue'
import { format } from 'date-fns'

const props = withDefaults(
	defineProps<{
		search: string
		routeName?: string
		routeParams?: Record<string, string | number>
		userId?: number
	}>(),
	{
		routeName: 'account-details',
		routeParams: () => ({}),
		userId: undefined,
	},
)

const listState = useTokenBatchListState()
const { ApiTable, Column } = useApiTable(listState)
listState.defaultParams.with = ['purchase']

watch(
	() => props.userId,
	(userId) => {
		if (userId) {
			listState.defaultParams.fromRelation = {
				model: 'App\\Models\\User',
				id: userId,
				relation: 'tokenBatches',
			}
		} else {
			delete listState.defaultParams.fromRelation
		}
		listState.getList()
	},
	{ immediate: true },
)

function formatHuman(date: string) {
	const d = new Date(date)
	return format(d, 'PPpp')
}

watch(
	() => props.search,
	(val) => {
		const uuid = typeof val === 'string' ? val : undefined
		if (!uuid) {
			listState.query().save()
			listState.getList()
			return
		}
		listState
			.query()
			.whereHas('exports', (query) => {
				return query.where('uuid', '=', uuid)
			})
			.orWhere('uuid', '=', uuid)
			.orWhere('purchase_id', '=', uuid)
			.save()
		listState.getList()
	},
	{ immediate: true },
)
</script>

<style scoped lang="scss">
.token-batches-table {
	display: flex;
	flex-direction: column;
	gap: 8px;

	.token-batches-table__amount {
		display: flex;
		flex-direction: column;
		line-height: 1.1;
		gap: 4px;

		.token-batches-table__amount-strong {
			font-weight: 700;
		}

		.token-batches-table__amount-id {
			font-size: 11px;
			color: var(--text-color);
			opacity: 0.8;
			text-decoration: none;
			text-wrap: nowrap;
			font-family: monospace;

			&:hover {
				text-decoration: underline;
			}
		}
	}
}
</style>
